import React, { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Menu, X, LogOut, LayoutDashboard, ShoppingCart, ChefHat, Users,
  ClipboardList, Settings, Image, Moon, Sun, Contact, Share2, Gift, Globe,
  MessageCircle, ChevronRight, ChevronDown, SlidersHorizontal, Ticket, Route, Package, Clock,
  BarChart3, Truck, Store, Tag, Briefcase, Footprints
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import { SITE_NAME } from '../lib/brand';

interface AdminLayoutProps {
  children: ReactNode;
  currentPage?: string;
  onLogout?: () => void;
}

interface AdminUser {
  id: number;
  username: string;
  role: string;
  display_name: string;
}

type LucideIcon = React.ElementType;

// 選用組件的選單項經 MENU_ICONS 註冊表取 icon（name → component），
// 令 compose 注入的選單片段無需改動本檔的 import 區塊。
const MENU_ICONS: Record<string, LucideIcon> = {
  LayoutDashboard, ShoppingCart, ChefHat, Users, ClipboardList, Settings, Image,
  Contact, Share2, Gift, Globe, MessageCircle, SlidersHorizontal, Ticket, Route,
  Package, Clock, BarChart3, Truck, Store, Tag, Briefcase, Footprints,
};

interface MenuChildItem {
  id: string;
  label: string;
  icon: LucideIcon;
  roles: string[];
  path: string;
}

interface MenuItem extends MenuChildItem {
  children?: MenuChildItem[];
}

const ALL_MENU_ITEMS: MenuItem[] = [
  // ── 每日營運（直達）──
  { id: 'conversations', label: 'WhatsApp 對話', icon: MessageCircle, roles: ['super_admin', 'admin'], path: '/admin/conversations' },
  { id: 'dashboard', label: '儀表板', icon: LayoutDashboard, roles: ['super_admin', 'admin'], path: '/admin/dashboard' },
  { id: 'orders', label: '訂單管理', icon: ShoppingCart, roles: ['super_admin', 'admin'], path: '/admin/orders' },
  // @module:menu:top — 選用組件的頂層選單項由 compose 注入於此行之下
  { id: 'supplier', label: '供貨商管理', icon: MENU_ICONS.Truck, roles: ['super_admin', 'admin'], path: '/admin/supplier' },
  { id: 'referrals', label: '推薦網絡', icon: MENU_ICONS.Share2, roles: ['super_admin', 'admin'], path: '/admin/referrals' },
  { id: 'offers', label: '優惠管理', icon: MENU_ICONS.Ticket, roles: ['super_admin', 'admin'], path: '/admin/offers' },

  // ── 商品與行銷（商店自身）──
  {
    id: 'store-group',
    label: '商品與行銷',
    icon: ChefHat,
    roles: ['super_admin', 'admin'],
    path: '/admin/products',
    children: [
      { id: 'products', label: '產品管理', icon: ChefHat, roles: ['super_admin', 'admin'], path: '/admin/products' },
      { id: 'promo-codes', label: '優惠碼', icon: Gift, roles: ['super_admin'], path: '/admin/promo-codes' },
      // @module:menu:store-group — 選用組件（如廣播推廣）的子選單由 compose 注入於此行之下
      { id: 'package-configs', label: '套餐配置', icon: MENU_ICONS.Package, roles: ['super_admin', 'admin'], path: '/admin/package-configs' },
    ]
  },

  // ── 數據 ──
  {
    id: 'analytics-group',
    label: '數據分析',
    icon: BarChart3,
    roles: ['super_admin', 'admin'],
    path: '/admin/analytics',
    children: [
      { id: 'analytics', label: '成效總覽', icon: BarChart3, roles: ['super_admin', 'admin'], path: '/admin/analytics' },
      // @module:menu:analytics-group — 選用組件（如客戶蹤跡）的子選單由 compose 注入於此行之下
      { id: 'journeys', label: '客戶蹤跡', icon: MENU_ICONS.Footprints, roles: ['super_admin', 'admin'], path: '/admin/journeys' },
    ]
  },

  // ── 網站內容 ──
  { id: 'cms', label: '網頁內容管理', icon: Globe, roles: ['super_admin', 'admin'], path: '/admin/cms' },

  // ── 系統設置 ──
  {
    id: 'settings',
    label: '系統設置',
    icon: Settings,
    roles: ['super_admin', 'admin'],
    path: '/admin/settings',
    children: [
      { id: 'settings-basic', label: '基本設置', icon: SlidersHorizontal, roles: ['super_admin'], path: '/admin/settings' },
      { id: 'media-library', label: '媒體庫', icon: Image, roles: ['super_admin', 'admin'], path: '/admin/media-library' },
      { id: 'default-responses', label: '預設回應', icon: MessageCircle, roles: ['super_admin', 'admin'], path: '/admin/default-responses' },
      { id: 'users', label: '用戶管理', icon: Users, roles: ['super_admin', 'admin'], path: '/admin/users' },
      { id: 'audit-logs', label: '操作日誌', icon: ClipboardList, roles: ['super_admin'], path: '/admin/audit-logs' },
    ]
  },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, currentPage, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('admin_theme');
    return saved === 'dark';
  });
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
  // WhatsApp 斷線警示（被動遙測：連續發送失敗 → 紅色 banner）
  const [waDown, setWaDown] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('admin_user');
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch { /* ignore */ }
    }
  }, []);

  // 每 5 分鐘查一次 WhatsApp 連線狀態（sessionStorage 暫存，唔會逐頁重複打）
  useEffect(() => {
    const CACHE_KEY = 'x1493_wa_health';
    const check = async () => {
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const { ts, down } = JSON.parse(cached);
          if (Date.now() - ts < 5 * 60 * 1000) {
            setWaDown(!!down);
            return;
          }
        }
        const res = await apiFetch('/api/public/admin/whatsapp/status');
        if (!res.ok) return;
        const json = await res.json().catch(() => ({}));
        const data = json.data ?? json;
        const down = data.state === 'down';
        setWaDown(down);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), down }));
      } catch { /* 狀態查詢失敗唔好阻住後台運作 */ }
    };
    check();
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('admin_theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // Auto-expand submenu that contains the current route
  useEffect(() => {
    const parent = ALL_MENU_ITEMS.find(item =>
      item.children?.some(child => location.pathname.startsWith(child.path))
    );
    if (parent) {
      setOpenSubmenus(prev => new Set([...prev, parent.id]));
    }
  }, [location.pathname]);

  const filterMenu = (items: MenuItem[]): MenuItem[] => {
    return items.reduce<MenuItem[]>((acc, item) => {
      const allowed = user?.role ? item.roles.includes(user.role) : true;
      const children = item.children
        ? item.children.filter(child => (user?.role ? child.roles.includes(user.role) : true))
        : undefined;
      if (allowed || (children && children.length > 0)) {
        acc.push({ ...item, children: children && children.length > 0 ? children : undefined });
      }
      return acc;
    }, []);
  };

  const menuItems = filterMenu(ALL_MENU_ITEMS);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      if (confirm('確定要登出嗎？')) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    }
  };

  const isCurrentPage = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const toggleSubmenu = (id: string) => {
    setOpenSubmenus(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'super_admin': return '系統管理員';
      case 'admin': return '管理員';
      case 'supplier': return '產品供應商';
      default: return role || '';
    }
  };

  const findActiveLabel = (items: MenuItem[]): string | undefined => {
    for (const item of items) {
      if (item.children) {
        const child = item.children.find(c => isCurrentPage(c.path));
        if (child) return child.label;
      }
      if (isCurrentPage(item.path)) return item.label;
    }
    return undefined;
  };

  const renderDesktopMenu = (items: MenuItem[]) => {
    return items.map(item => {
      const Icon = item.icon;
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = openSubmenus.has(item.id);
      const isActive = isCurrentPage(item.path) || (hasChildren && item.children!.some(c => isCurrentPage(c.path)));

      return (
        <div key={item.id}>
          <button
            onClick={() => {
              if (hasChildren) {
                toggleSubmenu(item.id);
              } else {
                navigate(item.path);
              }
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              isActive
                ? 'bg-white text-red-700 shadow-md font-semibold dark:bg-gray-700 dark:text-red-400'
                : 'text-red-100 hover:bg-red-500 hover:text-white dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
            }`}
            title={!sidebarOpen ? item.label : ''}
          >
            <Icon size={20} className="flex-shrink-0" />
            {sidebarOpen && (
              <div className="flex items-center justify-between flex-1">
                <span className="text-sm">{item.label}</span>
                {hasChildren && (
                  isOpen ? <ChevronDown size={16} className="flex-shrink-0" /> : <ChevronRight size={16} className="flex-shrink-0" />
                )}
              </div>
            )}
          </button>
          {sidebarOpen && hasChildren && isOpen && (
            <div className="ml-3 pl-3 border-l border-red-500/30 dark:border-gray-600 space-y-1 mt-1">
              {item.children!.map(child => {
                const ChildIcon = child.icon;
                const childActive = isCurrentPage(child.path);
                return (
                  <button
                    key={child.id}
                    onClick={() => navigate(child.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                      childActive
                        ? 'bg-white/20 text-white font-medium dark:bg-gray-700 dark:text-red-400'
                        : 'text-red-100 hover:bg-red-500 hover:text-white dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                    }`}
                  >
                    <ChildIcon size={18} className="flex-shrink-0" />
                    <span className="text-sm">{child.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  const renderMobileMenu = (items: MenuItem[]) => {
    return items.map(item => {
      const Icon = item.icon;
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = openSubmenus.has(item.id);
      const isActive = isCurrentPage(item.path) || (hasChildren && item.children!.some(c => isCurrentPage(c.path)));

      return (
        <div key={item.id}>
          <button
            onClick={() => {
              if (hasChildren) {
                toggleSubmenu(item.id);
              } else {
                navigate(item.path);
                setMobileMenuOpen(false);
              }
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              isActive
                ? 'bg-white text-red-700 shadow-md font-semibold dark:bg-gray-700 dark:text-red-400'
                : 'text-red-100 hover:bg-red-500 hover:text-white dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
            }`}
          >
            <Icon size={20} className="flex-shrink-0" />
            <span className="text-sm">{item.label}</span>
            {hasChildren && (
              isOpen ? <ChevronDown size={16} className="ml-auto flex-shrink-0" /> : <ChevronRight size={16} className="ml-auto flex-shrink-0" />
            )}
          </button>
          {hasChildren && isOpen && (
            <div className="ml-4 space-y-1 mt-1">
              {item.children!.map(child => {
                const ChildIcon = child.icon;
                const childActive = isCurrentPage(child.path);
                return (
                  <button
                    key={child.id}
                    onClick={() => {
                      navigate(child.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                      childActive
                        ? 'bg-white/20 text-white font-medium dark:bg-gray-700 dark:text-red-400'
                        : 'text-red-100 hover:bg-red-500 hover:text-white dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                    }`}
                  >
                    <ChildIcon size={18} className="flex-shrink-0" />
                    <span className="text-sm">{child.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className={`flex h-screen ${isDark ? 'dark' : ''}`}>
      {/* Desktop Sidebar */}
      <div
        className={`hidden md:flex ${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-gradient-to-b from-red-600 to-red-800 text-white transition-all duration-300 flex-col shadow-lg dark:from-gray-900 dark:to-gray-950`}
      >
        {/* Logo/Header */}
        <div className="h-20 flex items-center justify-between px-4 border-b border-red-500/50 dark:border-gray-700">
          <div className={`flex items-center gap-2 ${!sidebarOpen && 'justify-center w-full'}`}>
            <div className="bg-white rounded-lg p-2">
              <LayoutDashboard className="text-red-600" size={20} />
            </div>
            {sidebarOpen && (
              <div className="flex flex-col">
                <span className="font-bold text-white text-sm">{SITE_NAME}</span>
                <span className="text-xs text-red-200 dark:text-gray-400">管理後台</span>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-red-200 hover:text-white transition-colors dark:text-gray-400"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* User Info */}
        {sidebarOpen && user && (
          <div className="px-4 py-3 border-b border-red-500/50 dark:border-gray-700">
            <div className="text-sm font-semibold">{user.display_name || user.username}</div>
            <div className="text-xs text-red-200 dark:text-gray-400">{getRoleLabel(user.role)}</div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto">
          {renderDesktopMenu(menuItems)}
        </nav>

        {/* Theme Toggle + Logout */}
        <div className="px-3 py-4 border-t border-red-500/50 dark:border-gray-700 space-y-2">
          <button
            onClick={() => setIsDark(!isDark)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-100 hover:bg-red-500 hover:text-white transition-all duration-200 dark:text-gray-300 dark:hover:bg-gray-700"
            title={!sidebarOpen ? (isDark ? '亮色模式' : '暗黑模式') : ''}
          >
            {isDark ? <Sun size={20} className="flex-shrink-0" /> : <Moon size={20} className="flex-shrink-0" />}
            {sidebarOpen && <span className="text-sm font-medium">{isDark ? '亮色模式' : '暗黑模式'}</span>}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-100 hover:bg-red-600 hover:text-white transition-all duration-200 dark:text-gray-300"
            title={!sidebarOpen ? '登出' : ''}
          >
            <LogOut size={20} className="flex-shrink-0" />
            {sidebarOpen && <span className="text-sm font-medium">登出</span>}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-64 bg-gradient-to-b from-red-600 to-red-800 text-white flex flex-col shadow-lg z-50 md:hidden dark:from-gray-900 dark:to-gray-950">
            {/* Mobile Sidebar Header */}
            <div className="h-20 flex items-center justify-between px-4 border-b border-red-500/50 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <div className="bg-white rounded-lg p-2">
                  <LayoutDashboard className="text-red-600" size={20} />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-white text-sm">{SITE_NAME}</span>
                  <span className="text-xs text-red-200 dark:text-gray-400">管理後台</span>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="text-red-200 hover:text-white transition-colors dark:text-gray-400"
              >
                <X size={20} />
              </button>
            </div>

            {/* Mobile User Info */}
            {user && (
              <div className="px-4 py-3 border-b border-red-500/50 dark:border-gray-700">
                <div className="text-sm font-semibold">{user.display_name || user.username}</div>
                <div className="text-xs text-red-200 dark:text-gray-400">{getRoleLabel(user.role)}</div>
              </div>
            )}

            {/* Mobile Navigation Menu */}
            <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto">
              {renderMobileMenu(menuItems)}
            </nav>

            {/* Mobile Theme Toggle + Logout */}
            <div className="px-3 py-4 border-t border-red-500/50 dark:border-gray-700 space-y-2">
              <button
                onClick={() => setIsDark(!isDark)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-100 hover:bg-red-500 hover:text-white transition-all duration-200 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {isDark ? <Sun size={20} className="flex-shrink-0" /> : <Moon size={20} className="flex-shrink-0" />}
                <span className="text-sm font-medium">{isDark ? '亮色模式' : '暗黑模式'}</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-100 hover:bg-red-600 hover:text-white transition-all duration-200 dark:text-gray-300"
              >
                <LogOut size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">登出</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-gray-100 dark:bg-gray-900 dark:text-gray-300 transition-colors">
        {/* Top Bar */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm px-4 md:px-6 py-4 flex items-center justify-between transition-colors">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 -ml-2 text-gray-600 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400"
              aria-label="打開選單"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white truncate">
              {findActiveLabel(menuItems) || `${SITE_NAME} 管理後台`}
            </h1>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 hidden sm:block">
            {new Date().toLocaleDateString('zh-HK', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>

        {/* WhatsApp 斷線警示 banner */}
        {waDown && (
          <div className="bg-red-600 text-white px-4 md:px-6 py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-bold flex items-center gap-2">
              <MessageCircle size={16} /> WhatsApp 連線疑似中斷：連續發送失敗，客戶訊息收發可能已停止。
            </span>
            <button
              onClick={() => navigate('/admin/settings')}
              className="bg-white text-red-700 px-3 py-1 rounded-lg text-xs font-bold hover:bg-red-50 transition"
            >
              前往 設定 → WhatsApp 連線 處理
            </button>
          </div>
        )}

        {/* Page Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-4 md:p-6 min-w-0 w-full">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
